/**
 * Super Calculator admin Analytics Dashboard — Overview.
 *
 * Mirrors the Overview tab of the Super Picker dialog
 * (`super-picker/dialogs/admin/AnalyticsDashboardDialog.tsx`), reading the same
 * GA4 export but scoped to the `super_calculator_*` event family. The dimension
 * that replaces Super Picker's `project_name` is the `calculator` event param,
 * so "Top Viewed Projects" becomes "Top Browsing Calculators" and its drill-down
 * lists the agents who viewed that calculator.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type SuperCalculatorAnalyticsGranularity,
  type SuperCalculatorOverviewResponse,
  type SuperCalculatorStat,
  type SuperCalculatorViewerRow,
} from '../../super-picker/services/api';
import {
  DateRangeSelector,
  DEFAULT_DATE_RANGE_PRESETS,
  calendarToday,
  formatDisplayRange,
  formatInputDate,
  isBeforeDate,
  parseInputDate,
  type DateRangePreset,
  type DateRangeState,
} from '../../super-picker/dialogs/admin/DateRangeSelector';

interface AnalyticsDashboardDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Per-period bars vs a running total — applied to every time series below. */
type SeriesMode = 'period' | 'cumulative';
/** Which measure the Top Browsing Calculators chart plots. */
type CalculatorMetric = 'agents' | 'viewers' | 'views' | 'activities';
type ViewerSortField = 'ceaNumber' | 'name' | 'mobile' | 'views' | 'activities' | 'lastViewedOn';
type SortDirection = 'asc' | 'desc';

const CHART_COLORS = ['#ED8936', '#4299E1', '#38B2AC', '#ECC94B', '#9F7AEA', '#F56565', '#63B3ED'];

/**
 * Super Calculator started emitting GA4 events on 1 Apr 2026, so that is both the
 * default range start and the "All time" lower bound.
 */
const CALCULATOR_ANALYTICS_START = '2026-04-01';

/** GA4 `calculator` param values → the labels used in the navigation menu. */
const CALCULATOR_LABELS: Record<string, string> = {
  affordability: 'Max Affordability Calculator',
  buying_calculator: 'Buying Calculator',
  selling_calculator: 'Selling Calculator',
  decoupling_calculator: 'Decoupling Calculator',
  purchase_planner: 'Purchase Planner',
  mortgage_loan: 'Mortgage Loan',
  profile_manager: 'Manage Profile',
};

const CALCULATOR_METRICS: Array<{
  value: CalculatorMetric;
  label: string;
  short: string;
  help: string;
  /** Distinct-agent measures cannot be accumulated across buckets. */
  cumulative: boolean;
}> = [
  {
    value: 'agents',
    label: 'People',
    short: 'People',
    help: 'Distinct agents who used this calculator — each person counted once, however often they came back.',
    cumulative: false,
  },
  {
    value: 'views',
    label: 'Views',
    short: 'Views',
    help: 'Every arrival on this calculator, repeats included.',
    cumulative: true,
  },
  {
    value: 'activities',
    label: 'Activities',
    short: 'Activities',
    help: 'Every tracked event: views, menu clicks, PDF exports, tab/mode switches, history saves & loads.',
    cumulative: true,
  },
];

function metricMeta(metric: CalculatorMetric) {
  return CALCULATOR_METRICS.find((m) => m.value === metric) ?? CALCULATOR_METRICS[0];
}

function calculatorLabel(key: string): string {
  return CALCULATOR_LABELS[key] ?? key.replace(/_/g, ' ');
}

function createDefaultRange(): DateRangeState {
  const today = calendarToday();
  const start = parseInputDate(CALCULATOR_ANALYTICS_START);
  const end = start && isBeforeDate(today, start) ? start : today;
  return { startDate: CALCULATOR_ANALYTICS_START, endDate: formatInputDate(end) };
}

const CALCULATOR_DATE_PRESETS: DateRangePreset[] = [
  ...DEFAULT_DATE_RANGE_PRESETS,
  { id: 'allTime', label: 'All time', build: () => createDefaultRange() },
];

const chartCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
};

const headerCellSx = {
  fontWeight: 600,
  color: 'rgba(var(--color-text-rgb), 0.85)',
  bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
};

const toggleGroupSx = {
  height: 40,
  '& .MuiToggleButton-root': {
    px: 2,
    textTransform: 'none',
    borderColor: 'rgba(var(--color-text-rgb), 0.18)',
    color: 'rgba(var(--color-text-rgb), 0.7)',
  },
  '& .MuiToggleButton-root.Mui-selected': {
    color: 'var(--color-accent)',
    bgcolor: 'rgba(237,137,54,0.14)',
    '&:hover': { bgcolor: 'rgba(237,137,54,0.2)' },
  },
};

const formatCount = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }
  return value.toLocaleString();
};

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Series labels arrive as `YYYY-MM` (monthly) or `YYYY-MM-DD` (daily); monthly reads better as `Apr 2026`. */
const formatSeriesLabel = (label: string | number | null | undefined): string => {
  const text = String(label ?? '');
  const match = /^(\d{4})-(\d{2})$/.exec(text);
  if (!match) {
    return text;
  }
  const month = MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return month ? `${month} ${match[1]}` : text;
};

type ChartTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ value?: number | string | null; color?: string; name?: string }>;
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(20, 28, 43, 0.96)',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.72)' }}>
        Date
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
        {formatSeriesLabel(label) || '-'}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {payload.map((entry, index) => {
          const raw = entry?.value;
          const count = typeof raw === 'number' ? raw : Number(raw || 0);
          return (
            <Box
              key={`${entry?.name || 'value'}-${index}`}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography variant="caption" sx={{ color: entry?.color || 'rgba(255,255,255,0.72)' }}>
                {entry?.name || 'Count'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: entry?.color || '#63B3ED' }}>
                {formatCount(Number.isFinite(count) ? count : 0)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default function AnalyticsDashboardDialog({ open, onClose }: AnalyticsDashboardDialogProps) {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const chartGridStroke = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(31,41,68,0.12)';
  const chartAxisStroke = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(31,41,68,0.55)';
  const chartTickStyle = useMemo(
    () => ({ fill: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(31,41,68,0.75)', fontSize: 12 }),
    [isDark],
  );

  const [range, setRange] = useState<DateRangeState>(() => createDefaultRange());
  const [granularity, setGranularity] = useState<SuperCalculatorAnalyticsGranularity>('day');
  const [seriesMode, setSeriesMode] = useState<SeriesMode>('period');
  const [data, setData] = useState<SuperCalculatorOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usersOpen, setUsersOpen] = useState(true);
  const [activitiesOpen, setActivitiesOpen] = useState(true);
  const [calculatorTrendOpen, setCalculatorTrendOpen] = useState(false);
  const [calculatorMetric, setCalculatorMetric] = useState<CalculatorMetric>('agents');

  // Drill-down: who viewed one calculator over the selected range.
  const [drillCalculator, setDrillCalculator] = useState<SuperCalculatorStat | null>(null);
  const [viewers, setViewers] = useState<SuperCalculatorViewerRow[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersError, setViewersError] = useState('');
  const [viewerFilter, setViewerFilter] = useState('');
  const [viewerSortField, setViewerSortField] = useState<ViewerSortField>('views');
  const [viewerSortDir, setViewerSortDir] = useState<SortDirection>('desc');
  const [viewersTruncated, setViewersTruncated] = useState<{ truncated: boolean; limit: number } | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.getCalculatorAdminAnalyticsOverview({
        startDate: range.startDate,
        endDate: range.endDate,
        granularity,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics overview');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.startDate, range.endDate, granularity]);

  useEffect(() => {
    if (!open) return;
    void fetchOverview();
  }, [open, fetchOverview]);

  useEffect(() => {
    if (open) return;
    setRange(createDefaultRange());
    setGranularity('day');
    setSeriesMode('period');
    setCalculatorMetric('agents');
    setData(null);
    setError('');
    setDrillCalculator(null);
    setViewers([]);
    setViewersTruncated(null);
    setViewerFilter('');
  }, [open]);

  const fetchViewers = useCallback(
    async (calculator: string) => {
      setViewersLoading(true);
      setViewersError('');
      try {
        const response = await api.getCalculatorAdminCalculatorViewers({
          calculator,
          startDate: range.startDate,
          endDate: range.endDate,
        });
        setViewers(response.viewers);
        setViewersTruncated(
          response.truncated ? { truncated: true, limit: response.limit ?? response.viewers.length } : null,
        );
      } catch (err) {
        setViewersError(err instanceof Error ? err.message : 'Failed to load calculator viewers');
        setViewers([]);
        setViewersTruncated(null);
      } finally {
        setViewersLoading(false);
      }
    },
    [range.startDate, range.endDate],
  );

  useEffect(() => {
    if (!drillCalculator) return;
    void fetchViewers(drillCalculator.calculator);
  }, [drillCalculator, fetchViewers]);

  const isMonthly = data?.granularity === 'month';
  const isCumulative = seriesMode === 'cumulative';

  const topCalculator = data?.topCalculators?.[0] ?? null;

  /** New users per bucket + running total, joined with per-bucket active users. */
  const usersSeries = useMemo(() => {
    const byLabel = new Map<string, { label: string; activeUsers: number; newUsers: number }>();
    for (const point of data?.uniqueUserSeries ?? []) {
      const existing = byLabel.get(point.label);
      byLabel.set(point.label, {
        label: point.label,
        activeUsers: Number(point.users || 0),
        newUsers: existing?.newUsers ?? 0,
      });
    }
    for (const point of data?.signUpSeries ?? []) {
      const existing = byLabel.get(point.label);
      byLabel.set(point.label, {
        label: point.label,
        activeUsers: existing?.activeUsers ?? 0,
        newUsers: Number(point.users || 0),
      });
    }
    let running = 0;
    return Array.from(byLabel.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((point) => {
        running += point.newUsers;
        return { ...point, cumulativeNewUsers: running };
      });
  }, [data]);

  const activitySeries = useMemo(() => {
    let running = 0;
    return [...(data?.activitySeries ?? [])]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((point) => {
        const activities = Number(point.activities || 0);
        running += activities;
        return { label: point.label, activities, cumulativeActivities: running };
      });
  }, [data]);

  const metricInfo = metricMeta(calculatorMetric);
  /** Distinct-agent metrics have no meaningful running total, so cumulative is ignored for them. */
  const trendAccumulates = isCumulative && metricInfo.cumulative;

  const sortedCalculators = useMemo(
    () => [...(data?.topCalculators ?? [])].sort((a, b) => b[calculatorMetric] - a[calculatorMetric]),
    [data, calculatorMetric],
  );

  /** Wide rows (one per bucket, one column per calculator) for the multi-line trend. */
  const calculatorTrend = useMemo(() => {
    const keys = sortedCalculators.slice(0, CHART_COLORS.length).map((c) => c.calculator);
    if (keys.length === 0) return { keys, rows: [] as Array<Record<string, string | number>> };

    const labels = Array.from(new Set((data?.calculatorSeries ?? []).map((p) => p.label))).sort((a, b) =>
      a.localeCompare(b),
    );
    const byLabel = new Map<string, Record<string, number>>();
    for (const point of data?.calculatorSeries ?? []) {
      const bucket = byLabel.get(point.label) ?? {};
      bucket[point.calculator] = (bucket[point.calculator] ?? 0) + Number(point[calculatorMetric] || 0);
      byLabel.set(point.label, bucket);
    }

    const running: Record<string, number> = {};
    const rows = labels.map((label) => {
      const bucket = byLabel.get(label) ?? {};
      const row: Record<string, string | number> = { label };
      for (const key of keys) {
        const value = bucket[key] ?? 0;
        running[key] = (running[key] ?? 0) + value;
        row[key] = trendAccumulates ? running[key] : value;
      }
      return row;
    });
    return { keys, rows };
  }, [data, sortedCalculators, calculatorMetric, trendAccumulates]);

  const handleViewerSort = useCallback(
    (field: ViewerSortField) => {
      setViewerSortDir((prev) => (viewerSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
      setViewerSortField(field);
    },
    [viewerSortField],
  );

  const filteredViewers = useMemo(() => {
    const lower = viewerFilter.trim().toLowerCase();
    const rows = lower
      ? viewers.filter(
          (row) =>
            row.ceaNumber.toLowerCase().includes(lower) ||
            (row.name || '').toLowerCase().includes(lower) ||
            (row.mobile || '').toLowerCase().includes(lower),
        )
      : [...viewers];

    return rows.sort((a, b) => {
      let cmp = 0;
      switch (viewerSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'activities':
          cmp = a.activities - b.activities;
          break;
        case 'lastViewedOn':
          cmp = (a.lastViewedOn || '').localeCompare(b.lastViewedOn || '');
          break;
        case 'views':
        default:
          cmp = a.views - b.views;
          break;
      }
      return viewerSortDir === 'asc' ? cmp : -cmp;
    });
  }, [viewers, viewerFilter, viewerSortField, viewerSortDir]);

  const viewerTotals = useMemo(
    () =>
      filteredViewers.reduce(
        (acc, row) => ({ views: acc.views + row.views, activities: acc.activities + row.activities }),
        { views: 0, activities: 0 },
      ),
    [filteredViewers],
  );

  const sortableHeader = (field: ViewerSortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sx={headerCellSx} sortDirection={viewerSortField === field ? viewerSortDir : false}>
      <TableSortLabel
        active={viewerSortField === field}
        direction={viewerSortField === field ? viewerSortDir : 'desc'}
        onClick={() => handleViewerSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)', minHeight: '78vh' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h6">Analytics Dashboard</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
                Super Calculator overview — users, activities, and calculator browsing.
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <DateRangeSelector
                label="Overview Range"
                range={range}
                onApply={setRange}
                presets={CALCULATOR_DATE_PRESETS}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={granularity}
                onChange={(_event, value: SuperCalculatorAnalyticsGranularity | null) => {
                  if (!value) return;
                  setGranularity(value);
                }}
                aria-label="Series granularity"
                sx={toggleGroupSx}
              >
                <ToggleButton value="day" aria-label="Daily buckets">
                  Daily
                </ToggleButton>
                <ToggleButton value="month" aria-label="Monthly buckets">
                  Monthly
                </ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={seriesMode}
                onChange={(_event, value: SeriesMode | null) => {
                  if (!value) return;
                  setSeriesMode(value);
                }}
                aria-label="Series mode"
                sx={toggleGroupSx}
              >
                <ToggleButton value="period" aria-label="Per period totals">
                  {granularity === 'month' ? 'Per Month' : 'Per Day'}
                </ToggleButton>
                <ToggleButton value="cumulative" aria-label="Running totals">
                  Cumulative
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => void fetchOverview()}
                sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
              >
                Refresh
              </Button>
            </Box>

            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
              Tracking starts 1 Apr 2026, when Super Calculator began sending analytics events.
              {isCumulative
                ? ' Cumulative bars carry the running total through each bucket, so the last bucket matches the range total.'
                : ''}
            </Typography>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {data?.warnings?.map((warning) => (
              <Alert key={`overview-${warning}`} severity="warning">
                {warning}
              </Alert>
            ))}

            {loading ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card sx={chartCardSx}>
                      <CardContent sx={{ p: '0 !important' }}>
                        <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          Active Users
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {formatCount(data?.uniqueUsers)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                          Distinct agents who used a calculator in the selected period.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={chartCardSx}>
                      <CardContent sx={{ p: '0 !important' }}>
                        <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          New Users
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {formatCount(data?.newUsers)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                          Agents whose first-ever Super Calculator activity falls in this period.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        ...chartCardSx,
                        background: 'linear-gradient(135deg, rgba(237,137,54,0.18), rgba(237,137,54,0.04))',
                        borderColor: 'rgba(237,137,54,0.18)',
                      }}
                    >
                      <CardContent sx={{ p: '0 !important' }}>
                        <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          Top Calculator
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.75 }}>
                          {topCalculator ? calculatorLabel(topCalculator.calculator) : 'No data'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mt: 1 }}>
                          {topCalculator
                            ? `${formatCount(topCalculator.agents)} agents · ${formatCount(topCalculator.views)} views`
                            : 'No calculator activity in this period.'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* New Users */}
                <Box sx={chartCardSx}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setUsersOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setUsersOpen((prev) => !prev);
                      }
                    }}
                    aria-expanded={usersOpen}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      mb: usersOpen ? 2 : 0,
                      '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
                    }}
                  >
                    {usersOpen ? (
                      <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {isCumulative ? 'Cumulative New Users' : 'New Users'}
                    </Typography>
                  </Box>
                  <Collapse in={usersOpen} unmountOnExit>
                    <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={usersSeries}>
                          <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            stroke={chartAxisStroke}
                            tick={chartTickStyle}
                            tickFormatter={formatSeriesLabel}
                          />
                          <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                          <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(99, 179, 237, 0.14)' }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar
                            dataKey={isCumulative ? 'cumulativeNewUsers' : 'newUsers'}
                            name={isCumulative ? 'Cumulative New Users' : 'New Users'}
                            fill="#4299E1"
                            radius={[6, 6, 0, 0]}
                            barSize={isMonthly ? 40 : 22}
                          />
                          {isCumulative ? null : (
                            <Line
                              type="monotone"
                              dataKey="activeUsers"
                              name="Active Users"
                              stroke="var(--color-accent)"
                              strokeWidth={3}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                    {isCumulative ? (
                      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                        Active users are distinct agents per bucket and cannot be summed, so that line is only
                        shown in the per-{isMonthly ? 'month' : 'day'} view.
                      </Typography>
                    ) : null}
                  </Collapse>
                </Box>

                {/* Activities */}
                <Box sx={chartCardSx}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setActivitiesOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setActivitiesOpen((prev) => !prev);
                      }
                    }}
                    aria-expanded={activitiesOpen}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      mb: activitiesOpen ? 2 : 0,
                      '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
                    }}
                  >
                    {activitiesOpen ? (
                      <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {isCumulative ? 'Cumulative Activities' : 'Activities'}
                    </Typography>
                  </Box>
                  <Collapse in={activitiesOpen} unmountOnExit>
                    <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={activitySeries} margin={{ top: 6, right: 8, left: 8, bottom: 6 }}>
                          <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            stroke={chartAxisStroke}
                            tick={chartTickStyle}
                            tickFormatter={formatSeriesLabel}
                          />
                          <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                          <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(237, 137, 54, 0.14)' }} />
                          <Bar
                            dataKey={isCumulative ? 'cumulativeActivities' : 'activities'}
                            name={isCumulative ? 'Cumulative Activities' : 'Activities'}
                            fill="#ED8936"
                            radius={[6, 6, 0, 0]}
                            barSize={isMonthly ? 40 : undefined}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                      Every tracked event: calculator views, menu clicks, PDF exports, tab/mode switches, and
                      history saves & loads.
                    </Typography>
                  </Collapse>
                </Box>

                {/* Top Browsing Calculators */}
                <Box sx={chartCardSx}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Top Browsing Calculators
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}
                      >
                        {metricInfo.help} Click a bar to see the individual agents.
                      </Typography>
                    </Box>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={calculatorMetric}
                      onChange={(_event, value: CalculatorMetric | null) => {
                        if (!value) return;
                        setCalculatorMetric(value);
                      }}
                      aria-label="Calculator metric"
                      sx={toggleGroupSx}
                    >
                      {CALCULATOR_METRICS.map((metric) => (
                        <ToggleButton key={metric.value} value={metric.value} aria-label={metric.label}>
                          {metric.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ width: '100%', height: 340, mt: 2 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={sortedCalculators.map((stat) => ({
                          ...stat,
                          label: calculatorLabel(stat.calculator),
                        }))}
                        layout="vertical"
                        margin={{ left: 12, right: 12 }}
                      >
                        <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                        <YAxis
                          type="category"
                          dataKey="label"
                          width={170}
                          stroke={chartAxisStroke}
                          tick={chartTickStyle}
                        />
                        <RechartsTooltip />
                        <Bar
                          dataKey={calculatorMetric}
                          name={metricInfo.label}
                          radius={[0, 6, 6, 0]}
                          cursor="pointer"
                          onClick={(entry: { payload?: SuperCalculatorStat }) => {
                            if (entry?.payload) setDrillCalculator(entry.payload);
                          }}
                        >
                          {sortedCalculators.map((stat, index) => (
                            <Cell key={stat.calculator} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {sortedCalculators.length > 0 ? (
                    <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)', mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellSx}>Calculator</TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                              People
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                              Views
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                              Activities
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                              Views / Person
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                              Activities / Person
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedCalculators.map((stat) => (
                            <TableRow key={stat.calculator} hover>
                              <TableCell>{calculatorLabel(stat.calculator)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCount(stat.agents)}
                              </TableCell>
                              <TableCell align="right">{formatCount(stat.views)}</TableCell>
                              <TableCell align="right">{formatCount(stat.activities)}</TableCell>
                              <TableCell align="right">
                                {stat.agents > 0 ? (stat.views / stat.agents).toFixed(1) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {stat.agents > 0 ? (stat.activities / stat.agents).toFixed(1) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={() => setDrillCalculator(stat)}>
                                  Drill in
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', mt: 1 }}>
                      No calculator activity in this period.
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', mt: 1, display: 'block' }}>
                    People counts are distinct agents over the whole selected range, so they cannot be added up
                    across calculators — one agent using three calculators is counted in all three.
                  </Typography>
                </Box>

                {/* Per-calculator trend */}
                <Box sx={chartCardSx}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setCalculatorTrendOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setCalculatorTrendOpen((prev) => !prev);
                      }
                    }}
                    aria-expanded={calculatorTrendOpen}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      mb: calculatorTrendOpen ? 2 : 0,
                      '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
                    }}
                  >
                    {calculatorTrendOpen ? (
                      <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {trendAccumulates
                        ? `Cumulative ${metricInfo.short} by Calculator`
                        : `${metricInfo.short} by Calculator`}
                    </Typography>
                  </Box>
                  <Collapse in={calculatorTrendOpen} unmountOnExit>
                    {calculatorTrend.rows.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                        No calculator views in this period.
                      </Typography>
                    ) : (
                      <Box sx={{ width: '100%', height: 320, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height={320}>
                          <LineChart data={calculatorTrend.rows}>
                            <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                            <XAxis
                              dataKey="label"
                              stroke={chartAxisStroke}
                              tick={chartTickStyle}
                              tickFormatter={formatSeriesLabel}
                            />
                            <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                            <RechartsTooltip content={<ChartTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {calculatorTrend.keys.map((key, index) => (
                              <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                name={calculatorLabel(key)}
                                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                    {isCumulative && !metricInfo.cumulative ? (
                      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                        "{metricInfo.label}" counts distinct agents per {isMonthly ? 'month' : 'day'} and cannot be
                        accumulated, so this chart stays per {isMonthly ? 'month' : 'day'}. Switch to Views or
                        Activities for a running total.
                      </Typography>
                    ) : null}
                  </Collapse>
                </Box>
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(drillCalculator)}
        onClose={() => setDrillCalculator(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Box>
              <Typography variant="h6">
                {drillCalculator ? calculatorLabel(drillCalculator.calculator) : 'Calculator'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(var(--color-text-rgb), 0.56)', display: 'block', mt: 0.5 }}
              >
                Agents who used this calculator over the Overview date range: {formatDisplayRange(range)}.
                Rows with 0 views interacted without an attributed view.
              </Typography>
            </Box>
            <IconButton onClick={() => setDrillCalculator(null)} size="small" sx={{ mt: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              placeholder="Filter by CEA, name, or mobile…"
              value={viewerFilter}
              onChange={(event) => setViewerFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'rgba(var(--color-text-rgb), 0.45)', fontSize: 20 }} />
                ),
              }}
              fullWidth
              sx={{ '& .MuiInputBase-input': { color: 'var(--color-text-strong)' } }}
            />

            {viewersError ? <Alert severity="error">{viewersError}</Alert> : null}

            {viewersLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : filteredViewers.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', py: 2 }}>
                No agents used this calculator in the selected period.
              </Typography>
            ) : (
              <>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
                    {formatCount(filteredViewers.length)} agents · {formatCount(viewerTotals.views)} views ·{' '}
                    {formatCount(viewerTotals.activities)} activities
                  </Typography>
                  {viewersTruncated ? (
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                      Showing the top {formatCount(viewersTruncated.limit)} viewers by view count — more agents
                      viewed this calculator in the selected range.
                    </Typography>
                  ) : null}
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)', maxHeight: 460 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {sortableHeader('ceaNumber', 'CEA Number')}
                        {sortableHeader('name', 'Name')}
                        {sortableHeader('mobile', 'Mobile')}
                        {sortableHeader('views', 'Views', 'right')}
                        {sortableHeader('activities', 'Activities', 'right')}
                        {sortableHeader('lastViewedOn', 'Last Viewed')}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredViewers.map((row) => (
                        <TableRow key={row.ceaNumber} hover>
                          <TableCell>{row.ceaNumber}</TableCell>
                          <TableCell>{row.name || '—'}</TableCell>
                          <TableCell>{row.mobile || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCount(row.views)}
                          </TableCell>
                          <TableCell align="right">{formatCount(row.activities)}</TableCell>
                          <TableCell>{row.lastViewedOn || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
